import { ScanOutlined } from '@ant-design/icons';
import { Alert, Button, Input, Modal, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useIsMobile } from '@/lib/useIsMobile';
import { createDecoder, palletFromScan, palletPath } from '@/lib/scan';

const SCAN_MS = 150; // пауза между кадрами: быстро для руки и не греет телефон

type Camera = 'starting' | 'on' | 'denied' | 'unavailable';

function stop(stream: MediaStream | null) {
  stream?.getTracks().forEach((t) => {
    t.stop();
  });
}

/**
 * Скан палета (T-203): камера телефона или ввод кода → карточка партии на этом палете,
 * там «Отгрузить» и «Списать» (T-204). Камере нужен HTTPS — на сервере он есть (Cloudflare).
 */
function ScannerModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mobile = useIsMobile();
  const video = useRef<HTMLVideoElement>(null);
  const [camera, setCamera] = useState<Camera>('starting');
  const [notPallet, setNotPallet] = useState<string | null>(null);
  const [manual, setManual] = useState('');

  /** true — это код палета, карточка открывается. */
  function open(text: string): boolean {
    const pallet = palletFromScan(text);
    if (!pallet) {
      setNotPallet(text.trim());
      return false;
    }
    if ('vibrate' in navigator) navigator.vibrate(60); // в Safari на iPhone вибрации нет
    onClose();
    void navigate(palletPath(pallet));
    return true;
  }
  // Цикл камеры живёт в эффекте; свежий обработчик — через ref, без перезапуска камеры.
  const onCode = useRef(open);
  onCode.current = open;

  useEffect(() => {
    // Флаг читается через функцию: иначе TypeScript «сужает» его до false и не видит очистку.
    const run = { stopped: false };
    const stopped = () => run.stopped;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    void (async () => {
      // Без HTTPS (и в старых браузерах) mediaDevices нет вовсе, хотя типы DOM обещают его.
      const media = navigator.mediaDevices as MediaDevices | undefined;
      if (!window.isSecureContext || !media) {
        setCamera('unavailable');
        return;
      }
      try {
        stream = await media.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch (e) {
        setCamera(
          e instanceof DOMException && e.name === 'NotAllowedError' ? 'denied' : 'unavailable',
        );
        return;
      }
      const el = video.current;
      if (stopped() || !el) {
        stop(stream);
        return;
      }
      el.srcObject = stream;
      await el.play().catch(() => undefined);
      setCamera('on');
      const decode = await createDecoder();
      const tick = async () => {
        if (stopped()) return;
        const text = el.readyState >= 2 ? await decode(el).catch(() => null) : null;
        if (stopped()) return;
        if (text && onCode.current(text)) return;
        timer = window.setTimeout(() => void tick(), SCAN_MS);
      };
      void tick();
    })();
    return () => {
      run.stopped = true;
      window.clearTimeout(timer);
      stop(stream);
    };
  }, []);

  return (
    <Modal open title={t('scan.title')} footer={null} onCancel={onClose} destroyOnHidden>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {camera === 'denied' && <Alert type="warning" showIcon message={t('scan.denied')} />}
        {camera === 'unavailable' && <Alert type="info" showIcon message={t('scan.unavailable')} />}
        {(camera === 'starting' || camera === 'on') && (
          <>
            <video
              ref={video}
              aria-label={t('scan.camera')}
              muted
              playsInline // iPhone: видео в окне, а не во весь экран
              style={{
                width: '100%',
                aspectRatio: '1',
                objectFit: 'cover',
                borderRadius: 8,
                background: '#000',
              }}
            />
            <Typography.Text type="secondary">{t('scan.hint')}</Typography.Text>
          </>
        )}
        {notPallet !== null && (
          <Alert type="error" showIcon message={t('scan.notPallet', { text: notPallet })} />
        )}
        <Input.Search
          aria-label={t('scan.manual')}
          placeholder="SWP-12-3"
          enterButton={t('scan.open')}
          size={mobile ? 'large' : 'middle'}
          // Компьютер: сразу в поле — сканер-«пистолет» печатает код сам. Телефон: без клавиатуры.
          autoFocus={!mobile}
          value={manual}
          onChange={(e) => {
            setManual(e.target.value);
          }}
          onSearch={(v) => {
            if (v.trim()) open(v);
          }}
        />
      </Space>
    </Modal>
  );
}

/** Кнопка в шапке: на телефоне — значок, на компьютере — с подписью. */
export function ScanButton({ compact }: { compact: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type={compact ? 'text' : 'default'}
        size={compact ? 'large' : 'middle'}
        icon={<ScanOutlined />}
        aria-label={t('scan.button')}
        onClick={() => {
          setOpen(true);
        }}
      >
        {compact ? null : t('scan.button')}
      </Button>
      {open && (
        <ScannerModal
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
}
